import * as React from 'react'

import { DriverModel } from '@/app/models'
import { IdentityDocumentType } from '@/app/constants/identityDocumentType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const DriverBasicInfo = ({
  driver,
  groupNames,
  companyId,
  setIsOpenEditDriverModal,
}: {
  driver: DriverModel
  groupNames: string[]
  companyId: string
  setIsOpenEditDriverModal: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const { triggerRefresh } = useDrivers()
  const { formatDate } = useFormatDate()
  const { activateDriver } = useDriverService()
  const isActive = driver?.isActive

  const activateDriverHandler = async ({ active }: { active: boolean }) => {
    await activateDriver({
      driversIds: [driver?.driverId?.value],
      companyId: companyId,
      setActive: active,
    }).finally(() => {
      triggerRefresh()
    })
  }
  return (
    <section className="tw-relative tw-flex tw-flex-col">
      <div className="tw-absolute tw-right-0 tw-top-6 tw-flex tw-gap-2">
        <Tooltip
          text={`${isActive ? t('driverModule.deactivate') : t('driverModule.activate')}`}
          onClick={() => activateDriverHandler({ active: !isActive })}
        >
          <div className="tw-grid tw-cursor-pointer tw-rounded-lg tw-bg-secondary tw-p-2 hover:tw-opacity-90">
            {isActive ? (
              <FaIcon icon={faUserDashedRegular} className="tw-text-primary" />
            ) : (
              <FaIcon icon={faUserCheckSolid} className="tw-text-primary" />
            )}
          </div>
        </Tooltip>
        <Tooltip
          text={t('common.edit')}
          onClick={() => setIsOpenEditDriverModal((prev) => !prev)}
        >
          <div className="tw-grid tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-2 hover:tw-opacity-90">
            <FaIcon icon={faPencilSolid} className="tw-text-white" />
          </div>
        </Tooltip>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-8 tw-py-5 tw-pt-6 lg:tw-flex-row">
        <div
          className="tw-flex tw-w-full tw-flex-col tw-gap-2.5 lg:tw-max-w-[450px]"
          data-openreplay-masked
        >
          <ActiveBlock isActive={driver?.isActive} />

          <InfoBlock
            label={t('driverModule.work-start-date')}
            lines={[formatDate(driver?.workingStartTime)]}
          />
          <InfoBlock
            label={t('driverModule.groups')}
            lines={groupNames || ['-']}
          />
          <Divider />
          <InfoBlock
            label={t('driverModule.birth-date')}
            lines={[formatDate(driver?.birthday)]}
          />
          <InfoBlock
            label={t('driverModule.pesel-or-id')}
            lines={[driver?.pesel || driver?.driverId?.value]}
          />

          <InfoBlock
            label={t('driverModule.identity-document-type')}
            lines={[
              `identityDocumentType.${IdentityDocumentType[driver?.driverDocumentType]}`,
            ]}
            withTranslation
          />
          <InfoBlock
            label={t('driverModule.document-number')}
            lines={[driver?.driverIdCardNumber]}
          />
        </div>
        <div
          className="tw-flex tw-w-full tw-flex-col tw-gap-2.5 lg:tw-max-w-[450px]"
          data-openreplay-masked
        >
          <InfoBlock label="ID Import" lines={[driver?.customId]} />
          <InfoBlock label="ID Export" lines={[driver?.externalId]} />
          <InfoBlock label="E-mail" lines={[driver?.email]} />
          <Divider />
          <InfoBlock
            label={t('driverModule.phone')}
            lines={[driver?.phoneNumber]}
          />
          <InfoBlock
            label={t('driverModule.home-address')}
            lines={[
              driver?.address,
              [driver?.city, ' ', driver?.postalCode],

              driver?.country?.name,
            ]}
          />
        </div>
      </div>
    </section>
  )
}

export default DriverBasicInfo
import CheckmarkGreen from '../../../../../assets/icons_new/CheckmarkGreen.svg'
import Close from '../../../../../assets/icons_new/CloseGray.svg'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import useDriverService from '@/modules/drivers/services/DriverService'
import {
  FaIcon,
  faPencilSolid,
  faUserCheckSolid,
  faUserDashedRegular,
} from '@/lib/fa'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import {
  selectLoading,
  useLoadingStore,
} from '@/modules/shared/stores/loadingStore'

const ActiveBlock = ({ isActive }: { isActive: boolean }) => {
  const { t } = useLocalize() // TODO - ADD TRANSLATIONS
  return (
    <div className="tw-text-sm">
      <label className="tw-mb-0 tw-py-0.5 tw-font-medium">Status</label>
      <div>
        {isActive ? (
          <div className="tw-flex tw-items-center">
            <img src={CheckmarkGreen} alt="Checkmark Icon" />
            <p className="tw-text-sm tw-font-semibold tw-text-success">
              {t('common.active')}
            </p>
          </div>
        ) : (
          <div className="tw-flex tw-items-center">
            <img src={Close} alt="Close Icon" />
            <p className="tw-text-sm tw-font-semibold tw-text-content-base/70">
              {t('common.inactive')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
const InfoBlock = ({ label, lines, withTranslation = false }) => {
  const { t } = useLocalize()
  return (
    <>
      <div className="tw-text-sm">
        <label className="tw-mb-0 tw-py-0.5 tw-font-medium">{label}</label>
        <div>
          {lines.map((line: string, i) => (
            <div key={i}>{withTranslation ? t(line) : (line ?? '-')}</div>
          ))}
        </div>
      </div>
    </>
  )
}

const Divider = () => {
  return <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
}
